"use client";
import { useState, useEffect } from "react";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaMapMarkerAlt,
  FaCamera,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

export default function UserProfile() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [user, setUser] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    password: "",
    address: "",
    avatar: "",
  });

  const [avatarFile, setAvatarFile] = useState(null);

  // ✅ Get token & userId from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");
  const navigate = useNavigate();

  // ✅ Redirect if not logged in
  useEffect(() => {
    if (!authToken || !authUserId) {
      console.error("Auth token or userId not found in localStorage");
      navigate("/login");
    }
  }, [authToken, authUserId, navigate]);

  // ✅ Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (!authUserId || !authToken) return;
        setLoading(true);

        const response = await axios.get(
          `https://tyka.premierhostings.com/beta/api/users/${authUserId}`,
          { headers: { Authorization: `Bearer ${authToken}` } } // add token if needed
        );
        console.log("response.data.data",response.data.data);
        debugger

        if (response.data?.data) {
          const userData = response.data.data;
          setUser({
            name: userData.name || "",
            email: userData.email || "",
            phone: userData.phone || "",
            gender: userData.gender || "",
            password: "",
            address: userData.user_address,
            avatar: userData.avatar || "",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to Load User",
          text: error.response?.data?.message || error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [authUserId, authToken]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  // Handle avatar upload and preview
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const previewURL = URL.createObjectURL(file);
      setUser((prev) => ({ ...prev, avatar: previewURL }));
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setLoading(true);

      if (!user.name || !user.email || !user.phone) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Please fill Name, Email, and Phone before saving.",
        });
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("name", user.name);
      formData.append("email", user.email);
      formData.append("phone", user.phone);
      formData.append("gender", user.gender || "");
      formData.append("status", 1);

      if (user.password && user.password.trim() !== "") {
        formData.append("password", user.password);
      }

      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }

      formData.append("user_address[0][address]", user.address || "");

      const response = await axios.post(
        `https://tyka.premierhostings.com/beta/api/users/${authUserId}?_method=PUT`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.data) {
        const updatedData = response.data.data;
        setUser((prev) => ({
          ...prev,
          name: updatedData.name || prev.name,
          email: updatedData.email || prev.email,
          phone: updatedData.phone || prev.phone,
          gender: updatedData.gender || prev.gender,
          address:
            updatedData.user_address?.data?.length > 0
              ? updatedData.user_address.data[0].address || prev.address
              : prev.address,
          avatar: updatedData.avatar || prev.avatar,
          password: "",
          status: 1,
        }));
        setAvatarFile(null);
      }

      Swal.fire({
        icon: "success",
        title: "Profile Updated Successfully ✅",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Update Error:", error.response?.data || error.message);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/");

  return (
<div className="bg-gray-100 min-h-screen p-6 md:p-10">
  <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">

    {/* Left Profile Card */}
    <div className="bg-white rounded-2xl shadow-lg p-6 h-fit">
      <div className="flex flex-col items-center">

        <div className="relative">
          <img
            src={
              user.avatar ||
              "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
            }
            alt="Profile"
            className="w-36 h-36 rounded-full border-4 border-red-500 object-cover"
          />

          <label
            htmlFor="avatarUpload"
            className="absolute bottom-2 right-2 bg-red-600 text-white p-2 rounded-full cursor-pointer"
          >
            <FaCamera />

            <input
              type="file"
              id="avatarUpload"
              className="hidden"
              accept="image/*"
              onChange={handleImageUpload}
            />
          </label>
        </div>

        <h2 className="mt-5 text-2xl font-bold">
          {user.name}
        </h2>

        <p className="text-gray-500">
          {user.email}
        </p>

        <div className="w-full mt-8 space-y-4">

          <div className="flex justify-between border-b pb-2">
            <span className="font-medium">Phone</span>
            <span>{user.phone || "-"}</span>
          </div>

          <div className="flex justify-between border-b pb-2">
            <span className="font-medium">Gender</span>
            <span>{user.gender || "-"}</span>
          </div>

          {/* Address List */}
{user.user_address?.data?.length > 0 && (
  <div className="mt-8">
    <h2 className="text-2xl font-bold mb-4">Saved Addresses</h2>

    <div className="space-y-4">
      {user.user_address.data.map((address) => (
        <div
          key={address.id}
          className="border rounded-xl p-5 bg-gray-50 shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="font-semibold">{address.name}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-semibold">{address.email}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-semibold">
                {address.phone_number || "-"}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Address Type</p>
              <span className="inline-block px-3 py-1 rounded-full bg-red-100 text-red-600 text-sm capitalize">
                {address.address_type}
              </span>
            </div>

            <div className="md:col-span-2">
              <p className="text-sm text-gray-500">Street</p>
              <p className="font-semibold">{address.street}</p>
            </div>

            <div className="md:col-span-2">
              <p className="text-sm text-gray-500">Apartment / Suite</p>
              <p className="font-semibold">
                {address.apartment_suite || "-"}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">City</p>
              <p className="font-semibold">
                {address.city?.name || "-"}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">State</p>
              <p className="font-semibold">
                {address.state?.name || "-"}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Country</p>
              <p className="font-semibold">
                {address.country?.name || "-"}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Zip Code</p>
              <p className="font-semibold">{address.zipcode}</p>
            </div>

          </div>
        </div>
      ))}
    </div>
  </div>
)}

        </div>
      </div>
    </div>

    {/* Right Edit Form */}
    <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-8 relative">

      {loading && (
        <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center z-50">
          <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <h2 className="text-3xl font-bold text-red-600 mb-8">
        Edit Profile
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Name */}
        <div>
          <label className="font-medium mb-2 flex items-center gap-2">
            <FaUser />
            Full Name
          </label>

          <input
            type="text"
            name="name"
            value={user.name}
            onChange={handleChange}
            className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        {/* Email */}
        <div>
          <label className="font-medium mb-2 flex items-center gap-2">
            <FaEnvelope />
            Email
          </label>

          <input
            type="email"
            name="email"
            value={user.email}
            onChange={handleChange}
            className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="font-medium mb-2 flex items-center gap-2">
            <FaPhone className="rotate-90" />
            Phone
          </label>

          <input
            type="text"
            name="phone"
            value={user.phone}
            onChange={handleChange}
            className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        {/* Gender */}
        <div>
          <label className="font-medium mb-2">
            Gender
          </label>

          <select
            name="gender"
            value={user.gender}
            onChange={handleChange}
            className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-red-500 outline-none"
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Password */}
        <div className="md:col-span-2">
          <label className="font-medium mb-2 flex items-center gap-2">
            <FaLock />
            Password
          </label>

          <div className="relative">

            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={user.password}
              onChange={handleChange}
              placeholder="Enter new password"
              className="w-full border rounded-xl p-3 pr-12 focus:ring-2 focus:ring-red-500 outline-none"
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>

          </div>
        </div>

      </div>

      {/* Buttons */}

      <div className="flex justify-end gap-4 mt-8">

        <button
          onClick={handleCancel}
          className="px-6 py-3 rounded-xl bg-gray-200 hover:bg-gray-300"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          className="px-6 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>

      </div>

    </div>

  </div>
{/* Address List */}
{user.address?.data?.length > 0 && (
  <div className="mt-8">
    <h2 className="text-2xl font-bold mb-4">Saved Addresses</h2>

    <div className="space-y-4">
      {user.address.data.map((address) => (
        <div
          key={address.id}
          className="bg-white border rounded-xl p-5 shadow-sm"
        >
          {/* Address Type */}
          <div className="mb-4">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                address.address_type === "home"
                  ? "bg-green-100 text-green-700"
                  : address.address_type === "office"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {address.address_type}
            </span>
          </div>

          {/* Street, Apartment */}
          <div className="mb-3">
            <p className="text-sm text-gray-500">Address</p>
            <p className="font-medium text-gray-800">
              {address.street}
              {address.apartment_suite && `, ${address.apartment_suite}`}
            </p>
          </div>

          {/* City, State, Country, Pincode */}
          <div>
            <p className="text-sm text-gray-500">Location</p>
            <p className="font-medium text-gray-800">
              {[
                address.city?.name,
                address.state?.name,
                address.country?.name,
                address.zipcode,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
</div>
  );
}
